import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../AuthContext';
import { useApi } from '../ApiContext';

// Helper function to decode JWT token
function decodeJWT(token: string): any {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return null;
  }
}

// Тип профиля пользователя по структуре из сгенерированного API
export interface UserProfile {
  id?: string;
  display_name?: string;
  first_name?: string;
  last_name?: string;
  middle_name?: string;
  bio?: string;
  date_of_birth?: string;
  gender?: string;
  location?: string;
  language?: string;
  created_at?: string;
  updated_at?: string;
  is_banned?: boolean;
  ban_reason?: string;
  ban_comment?: string;
  ban_until?: string;
  banned_at?: string;
  is_default?: boolean;
  user_id?: string;
  platform_id?: string;
  timezone?: string;
  contacts?: { contacts?: Array<{ type?: string; value?: string }> };
  active_avatar_id?: string;
  notification_settings?: any;
  privacy_settings?: any;
  resources?: any;
  ui_settings?: any;
}

interface UserProfileContextProps {
  profile: UserProfile | null;
  setProfile: (profile: UserProfile | null) => void;
  updateProfile: (data: Partial<UserProfile>) => void;
  isLoading: boolean;
  reloadProfile: () => Promise<void>;
}

const UserProfileContext = createContext<UserProfileContextProps | undefined>(undefined);

export const UserProfileProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { isAuthenticated, accessToken } = useAuth();
  const api = useApi();
  const lastTokenRef = useRef<string | null>(null);
  
  // Debug logging
  useEffect(() => {
    console.log('UserProfile context:', { profile: profile ? 'loaded' : 'null', profileId: profile?.id });
  }, [profile]);

  const updateProfile = useCallback((data: Partial<UserProfile>) => {
    setProfile((prev) => prev ? { ...prev, ...data } : null);
  }, []);

  // Auto-load default profile when user is authenticated and token changes
  useEffect(() => {
    const loadDefaultProfile = async () => {
      if (
        isAuthenticated &&
        accessToken &&
        !profile &&
        !isLoading &&
        lastTokenRef.current !== accessToken
      ) {
        setIsLoading(true);
        lastTokenRef.current = accessToken;
        try {
          // Decode JWT to get user_id
          const payload = decodeJWT(accessToken);
          const userId = payload?.sub || payload?.user_id || payload?.id;
          if (!userId) {
            console.error('No user_id found in JWT token');
            return;
          }
          const profileRes = await api.UserprofileApi.getDefaultUserProfile(
            userId,
            undefined,
            undefined,
            { headers: { Authorization: `Bearer ${accessToken}` } }
          );
          setProfile(profileRes.data.profile || null);
        } catch (error) {
          console.error('Failed to load default profile:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };
    loadDefaultProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, accessToken, api]);

  const reloadProfile = useCallback(async () => {
    if (isAuthenticated && accessToken && profile && typeof profile.id === 'string') {
      setIsLoading(true);
      try {
        const profileRes = await api.UserprofileApi.getUserProfile(
          profile.id,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        setProfile(profileRes.data.profile || null);
      } catch (error) {
        console.error('Failed to reload profile:', error);
      } finally {
        setIsLoading(false);
      }
    }
  }, [isAuthenticated, accessToken, api, profile]);

  return (
    <UserProfileContext.Provider value={{ profile, setProfile, updateProfile, isLoading, reloadProfile }}>
      {children}
    </UserProfileContext.Provider>
  );
};

export function useUserProfile() {
  const ctx = useContext(UserProfileContext);
  if (!ctx) throw new Error('useUserProfile must be used within a UserProfileProvider');
  return ctx;
} 